import { TipoUsuarioPerfilOpcionEnum, TipoConsultaUsuarioEnum } from "../../modules/escritorio/types/enums/TipoUsuario.enum";
import { RolEnum } from "../types/enums/TipoRoles.enum";
import type { PerfilUsuarioSesion, PerfilEntidad } from "../types/interfaces/user.interface";

/**
 * Este manejador no registra la actividad en el sgc. Si se quiere hacer eso se debe usar el orquestador llamado `useActivityRegisterWhileDownloadFile`.
 * que internamente hace uso de esta funcion y realiza el registro de la accion emitida. 
 * De igual forma si se hace un cambio de los params aqui se debe aplicar alla.
 */
export const handleDownloadFile = async (params: { urlDocumento: string, sasToken?: string, nombreDocumento: string }) => {
  const { urlDocumento, sasToken = '', nombreDocumento } = params;
  if (urlDocumento) {
    try {
      // Extraer extensión de la URL
      // const urlParts = urlDocumento.split('.');
      const extensionUrl = getExtension(urlDocumento)/*urlParts.pop()?.split('?')[0].toLowerCase() || ''*/;

      // Tipos de archivo que requieren SAS token
      const multimediaExtensions = ['mp3', 'mp4', 'avi', 'png', 'jpeg', 'jpg'];

      // Construir URL con SAS token si es necesario
      let downloadUrl = urlDocumento;
      if (extensionUrl && multimediaExtensions.includes(extensionUrl)) {

        if (sasToken) {
          // Agregar SAS token si la URL no lo tiene ya
          const separator = urlDocumento.includes('?') ? '&' : '?';
          downloadUrl = `${urlDocumento}${separator}${sasToken}`;
        }
      }

      // Combinar nombre del documento con la extensión
      const extensionNombreDocumento = getExtension(nombreDocumento);
      const fileName = nombreDocumento ? extensionNombreDocumento ? nombreDocumento : `${nombreDocumento}.${extensionUrl}` : extensionUrl ? `archivo.${extensionUrl}` : 'archivo';

      // Obtener el archivo desde la URL
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Crear URL del blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Crear enlace de descarga con el nombre correcto
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;

      // Agregar al DOM, hacer clic y limpiar
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar el objeto URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      // Fallback: abrir en nueva pestaña
      window.open(urlDocumento, '_blank');
    }
  }
};

/**
 * @description Obtiene el tipo de consulta de usuario basado en el perfil de la entidad
 * @param {PerfilEntidad} perfil - Perfil de la entidad del usuario
 * @param {number} idUsuario - Id del usuario
 * @returns {PerfilUsuarioSesion} - Tipo de consulta de usuario
 */
export const obtenerTipoUsuario = (perfil: PerfilEntidad, idUsuario: number): PerfilUsuarioSesion => {

  const opcion = perfil.opcion;

  if (opcion === TipoUsuarioPerfilOpcionEnum.ENTIDAD) {

    const rol = perfil?.infoEntidad?.relacion.idRol;

    if ([RolEnum.Abogado, RolEnum.Paralegal, RolEnum.MinisterioPublico].includes(rol!)) {
      return {
        id: perfil.infoEntidad?.relacion.idRelUsuarioEntidades ?? 0,
        tipoConsulta: TipoConsultaUsuarioEnum.ABOGADO_PARALEGAL,
        idRol: RolEnum.Abogado,
        idEntidad: perfil.infoEntidad?.idEntidad,
        opcionPerfil: perfil.opcion
      };
    }

    if (rol === RolEnum.Administrador) {
      return {
        id: perfil.infoEntidad?.relacion.idRelUsuarioEntidades ?? 0,
        tipoConsulta: TipoConsultaUsuarioEnum.ENTIDAD,
        idRol: rol,
        idEntidad: perfil.infoEntidad?.idEntidad,
        opcionPerfil: perfil.opcion
      };
    }
  }

  return {
    id: idUsuario,
    tipoConsulta: TipoConsultaUsuarioEnum.USUARIO,
    opcionPerfil: perfil.opcion
  };

}
/**
 * @description Obtiene la extensión de un archivo
 * @param {string} url - URL del archivo
 * @returns {string} - Extensión del archivo
 */
export const getExtension = (url: string): string => {
  const parts = url.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase().split(/\#|\?/)[0] : '';
}

/**
 * Diccionario que mapea IdCalidad a IdTipoCalidad.
 * El IdTipoCalidad es un valor que representa el tipo de calidad de una parte en un trámite judicial.
 * Por ejemplo, un IdCalidad = 1 corresponder a un IdTipoCalidad = 2, lo que indica que la parte es un demandante.
 */
export const CalidadATipoCalidad: Record<number, number | null> = {
  1: 2, 2: 1, 3: 1, 4: 2, 5: 2, 6: 1, 7: 2, 8: 1,
  9: 2, 10: 2, 11: 2, 12: 7, 13: 2, 14: 1, 15: 2, 16: 1,
  17: 9, 18: 2, 19: 1, 20: 2, 21: 1, 22: 6, 23: 1, 24: 1,
  25: 2, 26: 1, 27: 1, 28: 2, 29: 1, 30: 2, 31: 6, 32: 2,
  33: 1, 34: 8, 35: 2, 36: 6, 37: 2, 38: 6, 39: 2, 40: 2,
  41: 2, 42: 2, 43: 1, 44: 1, 45: 2, 46: 1, 47: 2, 48: 2,
  49: 7, 50: 7, 51: 7, 52: 1, 53: 7, 54: 2, 55: 2, 56: 2,
  57: 7, 58: 7, 59: 1, 60: 2, 61: 2, 62: 2, 63: 2, 64: 2,
  65: 2, 66: 1, 67: 1, 68: 7, 69: 7, 70: 7, 71: 7, 72: 7,
  73: 7, 74: 6, 75: 8, 76: 7, 77: 7, 78: 1, 79: 8, 80: 7,
  81: 7, 82: 6, 83: 6, 84: 8, 85: 1, 86: 1, 87: 2,
  88: 8, 89: null, 90: null, 91: null, 92: 1,
  93: 6, 94: 6, 95: 7, 96: 7, 97: 2, 98: 7
};

/**
 * Diccionario que agrupa IdCalidad por IdTipoCalidad.
 * Este diccionario permite obtener todas las calidades asociadas a un tipo de calidad específico.
 * Por ejemplo, si se solicita el IdTipoCalidad 2, se obtendrán todas las IdCalidad que corresponden a ese tipo, como 1, 2, 4, etc.
 */
export const TipoCalidadACalidades: Record<number, number[]> = (() => {
  const result: Record<number, number[]> = {};
  for (const [key, value] of Object.entries(CalidadATipoCalidad)) {
    if (value !== null) {
      if (!result[value]) {
        result[value] = [];
      }
      result[value].push(Number(key));
    }
  }
  return result;
})();

/**
 * Mapeo de claves enteras a sus correspondientes descripciones de tipo de calidad.
 */
export const TipoCalidadDictionary: Record<number, string> = {
  1: "Demandado",
  2: "Demandante",
  6: "Componente Tribunal",
  7: "Participante",
  8: "Representante",
  9: "Depositante"
};

/**
 * Enum que define los tipos de calidad disponibles.
 */
export enum TipoCalidadEnum {
  DEMANDADO = 1,
  DEMANDANTE = 2,
  COMPONENTE_TRIBUNAL = 6,
  PARTICIPANTE = 7,
  REPRESENTANTE = 8,
  DEPOSITANTE = 9
}

// /**
//  * Define los grupos principales que determinan la exclusividad.
//  * Demandados (1) y Demandantes (2) son mutuamente excluyentes.
//  */
// export const GruposPrincipales = {
//   DEMANDADOS: TipoCalidadEnum.DEMANDADO,
//   DEMANDANTES: TipoCalidadEnum.DEMANDANTE
// } as const;

/**
 * Define los tipos de calidad que pueden vincularse a cualquier grupo principal.
 * Estos tipos son flexibles y se asocian al grupo principal de la primera parte seleccionada.
 */
export const TiposCalidadSecundarios = [TipoCalidadEnum.PARTICIPANTE, TipoCalidadEnum.DEPOSITANTE]; // Participante, Depositante

/**
 * Verifica si un idTipoCalidad corresponde a un grupo principal (Demandados o Demandantes).
 * @param idTipoCalidad - ID del tipo de calidad a verificar
 * @returns true si es grupo principal (1 o 2), false en caso contrario
 */
export const esGrupoPrincipal = (idTipoCalidad: number): boolean => {
  return idTipoCalidad === TipoCalidadEnum.DEMANDADO || idTipoCalidad === TipoCalidadEnum.DEMANDANTE;
};

/**
 * Verifica si un idTipoCalidad corresponde a un tipo secundario que puede vincularse a cualquier grupo.
 * @param idTipoCalidad - ID del tipo de calidad a verificar
 * @returns true si es tipo secundario (7, 9, etc.), false en caso contrario
 */
export const esTipoSecundario = (idTipoCalidad: number): boolean => {
  return TiposCalidadSecundarios.includes(idTipoCalidad);
};

/**
 * Determina el grupo real de una parte considerando su contexto en el caso.
 * Para tipos principales (1, 2), retorna el tipo directamente.
 * Para tipos secundarios, determina el grupo por las otras partes en el mismo contexto.
 * @param parte - Parte con idTipoCalidad e idCalidad
 * @param todasLasPartes - Array de todas las partes disponibles para determinar el contexto
 * @returns El grupo (1 o 2) al que pertenece la parte, o el propio idTipoCalidad si es principal
 */
export const determinarGrupoRealDeParte = (
  parte: { idTipoCalidad?: number | null; idCalidad?: number | null },
  todasLasPartes: Array<{ idTipoCalidad?: number | null; idCalidad?: number | null; value: string | number }>
): number | null => {
  if (!parte.idTipoCalidad) return null;
  
  // Si es tipo principal, ese es su grupo
  if (esGrupoPrincipal(parte.idTipoCalidad)) {
    return parte.idTipoCalidad;
  }
  
  // Para tipos secundarios, no hay forma directa de determinar el grupo sin contexto adicional
  // Por ahora, retornamos null para indicar que debe validarse de otra forma
  return null;
};

/**
 * Determina el grupo principal (Demandados o Demandantes) de una lista de partes.
 * Busca la primera parte que pertenezca a un grupo principal (tipo 1 o 2).
 * @param partes - Array de partes con su idTipoCalidad
 * @returns El idTipoCalidad del grupo principal encontrado, o null si no hay ninguno
 */
export const determinarGrupoPrincipal = (partes: Array<{ idTipoCalidad?: number | null }>): number | null => {
  const partePrincipal = partes.find(p => p.idTipoCalidad && esGrupoPrincipal(p.idTipoCalidad));
  return partePrincipal?.idTipoCalidad ?? null;
};

/**
 * Verifica si dos partes son compatibles para ser representadas juntas.
 * Dos partes son compatibles si:
 * 1. Ambas son del mismo tipo principal (1 o 2), O
 * 2. Una es principal y la otra es secundaria del mismo grupo, O
 * 3. Ambas son secundarias pero no hay grupo principal establecido aún
 * @param parte1 - Primera parte con idTipoCalidad
 * @param parte2 - Segunda parte con idTipoCalidad
 * @returns true si las partes son compatibles, false en caso contrario
 */
export const sonPartesCompatibles = (
  parte1: { idTipoCalidad?: number | null },
  parte2: { idTipoCalidad?: number | null }
): boolean => {
  if (!parte1.idTipoCalidad || !parte2.idTipoCalidad) return false;
  
  const tipo1 = parte1.idTipoCalidad;
  const tipo2 = parte2.idTipoCalidad;
  
  // Si ambas son del mismo tipo, son compatibles
  if (tipo1 === tipo2) return true;
  
  // Si ambas son principales pero diferentes (1 vs 2), NO son compatibles
  if (esGrupoPrincipal(tipo1) && esGrupoPrincipal(tipo2)) {
    return false; // Demandado no puede ir con Demandante
  }
  
  // Si una es principal y la otra secundaria, son compatibles
  // (el secundario se asociará al grupo del principal)
  if (esGrupoPrincipal(tipo1) && esTipoSecundario(tipo2)) return true;
  if (esTipoSecundario(tipo1) && esGrupoPrincipal(tipo2)) return true;
  
  // Si ambas son secundarias, son compatibles entre sí
  // (se asociarán al mismo grupo cuando se seleccione un principal)
  if (esTipoSecundario(tipo1) && esTipoSecundario(tipo2)) return true;
  
  // Otros casos no permitidos
  return false;
};

/**
 * Verifica si una parte puede vincularse al grupo establecido.
 * @param parte - Parte con idTipoCalidad a validar
 * @param partesYaSeleccionadas - Partes ya seleccionadas que establecen el grupo
 * @returns true si la parte puede vincularse, false en caso contrario
 */
export const puedeVincularseAlGrupo = (
  parte: { idTipoCalidad?: number | null },
  partesYaSeleccionadas: Array<{ idTipoCalidad?: number | null }>
): boolean => {
  if (!parte.idTipoCalidad) return false;
  
  // Si no hay partes seleccionadas, cualquier parte puede ser la primera
  if (partesYaSeleccionadas.length === 0) return true;
  
  // Verificar compatibilidad con todas las partes ya seleccionadas
  return partesYaSeleccionadas.every(parteSeleccionada => 
    sonPartesCompatibles(parte, parteSeleccionada)
  );
};

/**
 * Retorna el IdTipoCalidad dado un IdCalidad.
 */
export const ObtenerGrupoCalidadPorIdCalidad = (idCalidad: number): number | null => {
  return CalidadATipoCalidad[idCalidad] !== undefined ? CalidadATipoCalidad[idCalidad] : null;
};

/**
 * Retorna el nombre de la TipoCalidad según el IdTipoCalidad.
 */
export const ObtenerNombreTipoCalidad = (idTipoCalidad: number): string => {
  return TipoCalidadDictionary[idTipoCalidad] ?? "**No definido**";
};

/**
 * Retorna una lista de IdCalidad asociados a un IdTipoCalidad.
 */
export const ObtenerCalidadesPorIdTipoCalidad = (idTipoCalidad: number): number[] => {
  return TipoCalidadACalidades[idTipoCalidad] ?? [];
};

/**
 * Obtiene el tipo MIME basado en la extensión del archivo.
 * @param extension - Extensión del archivo (sin el punto, ej: 'pdf')
 * @returns Tipo MIME correspondiente
 */
export const obtenerMimeType = (extension: string): string => {
  switch (extension.toLowerCase()) {
    case 'pdf': return 'application/pdf';
    case 'avi': return 'video/avi';
    case 'mp4': return 'video/mp4';
    case 'dav': return 'video/x-dav';
    // case 'mov': return 'video/quicktime';
    // case 'wmv': return 'video/x-ms-wmv';
    case 'doc': return 'application/msword';
    case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png': return 'image/png';
    // case 'gif': return 'image/gif';
    default: return 'application/octet-stream';
  }
};

/**
 * Retorna un arreglo con todas las extensiones soportadas por obtenerMimeType.
 * @returns Arreglo de extensiones de archivo
 */
export const extensionesValidas = (): string[] => {
  return [
    'pdf',
    'avi',
    'mp4',
    'dav',
    // 'mov',
    // 'wmv',
    'doc',
    'docx',
    'jpg',
    'jpeg',
    'png'
  ];
};

/**
 * Retorna un arreglo con todos los tipos MIME únicos soportados por obtenerMimeType.
 * @returns Arreglo de tipos MIME
 */
export const mimeTypesValidos = (): string[] => {
  return [
    'application/pdf',
    'video/avi',
    'video/mp4',
    'video/x-dav',
    // 'video/quicktime',
    // 'video/x-ms-wmv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png'
  ];
};

/**
 * Enmascar cuenta de correo
 */
export const maskEmail = (email: string | undefined): string => {
  
  if (!email || !email.includes('@')) return '';

  const [local, domain] = email.split('@');

  if (local.length <= 2) return `${local[0]}***@${domain}`;

  const maskedLocal = `${local[0]}${'*'.repeat(5)}${local.slice(-1)}`;
  
  return `${maskedLocal}@${domain}`;
};